import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';

import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { SigninService } from 'src/app/auth/signin/signin.service';

import { Account } from 'src/app/core/auth/account.model';
import { AccountService } from 'src/app/core/auth/account.service';

import { environment } from '../../../environments/environment';
import { NgxSpinnerService } from 'ngx-spinner';
import { filter } from 'rxjs';
import { AuthServerProvider } from 'src/app/core/auth/auth-jwt.service';
import { MenuItem } from 'primeng/api';
import { RoleMenuService } from 'src/app/core/service/role-menu.service';
import { HostListener } from '@angular/core';
import { MasterDataService } from 'src/app/core/service/master-data.service';

@Component({
  selector: 'navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  standalone: false
})
export class NavbarComponent implements OnInit {
  inProduction?: boolean;
  isNavbarCollapsed = true;

  version = '';
  account: Account | null = null;
  unreadCount = 9;
  showTicketCenter = false;
  canUseTicketing = false;

  companyInfo = environment.companyInfo;

  mainMenu: MenuItem[] = [];

  profileMenuItems: MenuItem[] = [
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: () => {
        this.logout();
      }
    }
  ];

  isMobileMenuOpen = false;

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  // Track expanded mobile submenu items
  expandedMenus: { [key: string]: boolean } = {};

  // Toggle submenu open/close
  toggleSubMenu(label: string): void {
    this.expandedMenus[label] = !this.expandedMenus[label];
  }

  @HostListener('window:resize', [])
  onResize() {
    if (window.innerWidth > 992) {
      this.isMobileMenuOpen = false;
      this.expandedMenus = {};
    }
  }
  constructor(
    private loginService: SigninService,
    private sessionStorageService: SessionStorageService,
    private accountService: AccountService,
    private router: Router,
    protected localStorageService: LocalStorageService,
    protected spinner: NgxSpinnerService,
    private authServerProvider: AuthServerProvider,
    private http: HttpClient,
    private roleMenuService: RoleMenuService,
    private masterDataService: MasterDataService
  ) {
    this.version = environment.VERSION;
  }

  async ngOnInit(): Promise<void> {
    this.accountService.getAuthenticationState().subscribe((account) => {
      this.account = account;
    });
    this.masterDataService.getMasterData$().subscribe((masterData) => {
      this.buildMenu();
      this.updateTicketAccess(masterData);
    });
    this.buildMenu();
    this.updateTicketAccess(this.masterDataService.getMasterData());
    this.onResize();
  }

  buildMenu(): void {
    // Define all possible menus
    const allMenus: MenuItem[] = [
      { label: 'Dashboard', routerLink: '/home/dashboard' },
      { label: 'Rostering', routerLink: '/hrms/rostering' },
      {
        label: 'Attendance',
        items: [
          {
            label: 'Attendance View',
            routerLink: '/hrms/attendance/view',
          },
          {
            label: 'Regularisation',
            routerLink: '/hrms/attendance/regularisation',
          },
          {
            label: 'Monthly View',
            routerLink: '/hrms/attendance/monthlyView',
          },
          {
            label: 'Payroll Attendance',
            routerLink: '/hrms/attendance/payroll',
          },
        ],
      },
      {
        label: 'Role Mapping',
        routerLink: 'user-roles',
      },
      {
        label: 'Shift Planning',
        items: [
          { label: 'Master', routerLink: '/hrms/shift' },
          { label: 'Map-Contract', routerLink: '/hrms/shift/map-contract' },
        ],
      },
      {
        label: 'Workforce',
        items: [
          { label: 'Employee', routerLink: '/hrms/workforce' },
          { label: 'Onboarded Employee', routerLink: '/hrms/workforce/onboarding' },
          { label: 'Employee Transfer', routerLink: '/hrms/workforce/employee-transfer' },
        ],
      },
      {
        label: 'Client Operation',
        items: [
          { label: 'Customers', routerLink: '/customer' },
          { label: 'Sites', routerLink: '/customer/sites' },
          { label: 'Contracts', routerLink: '/customer/contracts' },
          { label: 'Billing Cycles', routerLink: '/customer/billing-cycles' },
        ],
      },
      { label: 'Attendance Policy', routerLink: '/hrms/policies' },
      { label: 'Reports', routerLink: '/reports' },
    ];

    // Only include menus the user can access
    const allowedMenus = this.roleMenuService.getAllowedMenus();
    console.log('ALLOWED MENUS: ', allowedMenus);
    this.mainMenu = allMenus.filter(menu => {
      // If menu has sub-items, check if any sub-item is allowed
      if (menu.items) {
        menu.items = menu.items.filter(sub => {
          if (typeof sub.label !== 'string') return false;
          let dbMenuName = sub.label;
          if (sub.label === 'Monthly View') {
            dbMenuName = 'Monthly Attendance View';
          }
          return allowedMenus.includes(dbMenuName);
        });
        return menu.items.length > 0;
      }
      // Reports is Admin only (enforced by the API as well), even if the menu is granted to another role
      if (menu.label === 'Reports' && !this.roleMenuService.isAdmin()) return false;
      // Selfie attendance policy administration is intentionally restricted to SA/Admin.
      if (menu.label === 'Attendance Policy') return this.roleMenuService.isAdmin();
      return typeof menu.label === 'string' && allowedMenus.includes(menu.label as string);
    });
  }

  collapseNavbar(): void {
    this.isNavbarCollapsed = true;
  }

  login(): void {
    this.router.navigate(['/login']);
  }

  logout(): void {
    this.collapseNavbar();

    // Get session state from storage (could be null if already expired)
    const sessionState =
      this.localStorageService.retrieve('sessionState') ??
      this.sessionStorageService.retrieve('sessionState');

    console.log('Session State for logout:', sessionState);

    // Prepare payload only if we have sessionState
    const loginPayload = { sessionState };

    this.http.post(`${environment.SERVER_API_URL}api/login/logout`, loginPayload).subscribe({
      next: (response) => {
        console.log('Logout API response:', response);
        this.performLocalLogout();
      },
      error: (error) => {
        console.error('Logout API call failed:', error);

        // If backend returns “session not found” or 404 treat as already logged out
        if (
          error?.status === 500 &&
          (error?.error?.message?.includes('Session not found') ||
            error?.error?.message?.includes('404 Not Found'))
        ) {
          console.warn('Session already invalid in server. Logging out locally.');
        }

        // In all cases clear client session
        this.performLocalLogout();
      }
    });
  }

  private performLocalLogout(): void {
    // Clear everything related to auth
    this.loginService.logout();
    this.localStorageService.clear('sessionState');
    this.sessionStorageService.clear('sessionState');

    // Navigate to login screen
    this.router.navigate(['/login']);
  }


  toggleNavbar(): void {
    this.isNavbarCollapsed = !this.isNavbarCollapsed;
  }

  onHome(): void {
    this.router.navigate(['/home']);
  }

  getUserProfileImage() {
    return this.localStorageService.retrieve('userProfileImageUrl');
  }

  preventDefault(event: Event) {
    event.preventDefault();
  }

  onNotifications() { }

  openTicketCenter(): void {
    if (this.canUseTicketing) this.showTicketCenter = true;
  }

  private updateTicketAccess(masterData: any): void {
    const allowedRoles = new Set(['SM', 'MS', 'SA', 'ADMIN']);
    const primaryRole = String(masterData?.employee?.role?.roleCode ?? '').toUpperCase();
    const assignmentRoles = (masterData?.employee?.roleAssignments ?? [])
      .map((assignment: any) => String(assignment?.role?.roleCode ?? '').toUpperCase());
    const hasTicketRole = allowedRoles.has(primaryRole) || assignmentRoles.some((role: string) => allowedRoles.has(role));
    this.canUseTicketing = hasTicketRole && this.roleMenuService.canAccessMenu('Support Tickets');
  }
}
